import { Position } from '@blueprintjs/core'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import keyBy from 'lodash.keyby'
import React, { useMemo, useState } from 'react'
import { FilterValues, useFilter } from '../packages/blueprint-components/src/useFilter'
import { Filter as OriginalFilter } from './../packages/blueprint-components/src/Filter'
import { FilterKeyName, FilterTags } from './FilterTags'

import { FilterI as FilterType } from '../constants/filter'

interface FilterProps {
    filters: FilterType[]
    defaultFilter?: any
    onChange?: any
    className?: ClassValue
    customFilters?: any
    saveFilter: any
    deleteCustomFilter: any
    editCustomFilter: any
    defaultActiveFilters?: any
    disabled?: boolean
    disableSavedFilters?: boolean
    menuPopoverPosition?: Position
    scrollable?: boolean
    hideTags?: boolean
    triggerClassName?: string
    maxTagWidth?: number
}

export const Filter = ({
    filters,
    defaultFilter,
    onChange,
    className,
    customFilters,
    saveFilter,
    deleteCustomFilter,
    editCustomFilter,
    defaultActiveFilters = {},
    disabled = false,
    disableSavedFilters = false,
    menuPopoverPosition,
    scrollable = false,
    hideTags = false,
    triggerClassName = '',
    maxTagWidth,
}: FilterProps) => {
    const [activeFilters, setActiveFilters] = useState<FilterValues>(defaultActiveFilters)
    const keyed = useMemo(() => {
        return keyBy(filters, 'key')
    }, [filters])
    const _onChange = (filters: FilterValues) => {
        const formattedFilters = formatFilters(filters)
        setActiveFilters(formattedFilters)
        onChange ? onChange(formattedFilters) : null
    }
    const [open, setOpen] = useState(false)

    // Here we create a filter hook so we can modify
    // the filters directly when a tag is closed
    const Filters = useFilter(_onChange, saveFilter)

    const toggleMenu = ({ state = null }) => {
        if (state != null) {
            setOpen(state)
        } else {
            setOpen(!open)
        }
    }

    const removeActiveFilter = (filterName: FilterKeyName) => {
        if (disabled) {
            return
        }

        Filters.remove({ key: filterName })
    }

    const additionalFilterData = useMemo(
        () => ({
            ...Filters.current,
            ...Filters.dirty,
        }),
        [Filters?.current, Filters?.dirty],
    )

    const memoizedTags = useMemo(() => {
        return (
            <FilterTags
                filters={keyed}
                isFilterMenuOpen={open}
                loading={disabled}
                activeFilters={activeFilters}
                removeActiveFilter={removeActiveFilter}
                setFiltersMenuState={(a) => toggleMenu({ state: a })}
                additionalFilterData={additionalFilterData}
                maxWidth={maxTagWidth}
            />
        )
    }, [keyed, activeFilters, open, disabled, additionalFilterData, maxTagWidth])

    return (
        <div className={classNames('d-flex align-items-center', className)}>
            <div className={hideTags ? '' : 'mr-2'}>
                <OriginalFilter
                    disabled={disabled}
                    filters={filters}
                    defaultFilter={defaultFilter}
                    deleteCustomFilter={deleteCustomFilter}
                    editCustomFilter={editCustomFilter}
                    customFilters={customFilters}
                    Filters={{
                        ...Filters,
                        activeFilters,
                    }}
                    enableSave={
                        !!Object.keys({
                            ...Filters.current,
                            ...Filters.dirty,
                        }).length
                    }
                    openState={open}
                    setOpenState={setOpen}
                    disableSavedFilters={disableSavedFilters}
                    menuPopoverPosition={menuPopoverPosition}
                    scrollable={scrollable}
                    triggerClassName={triggerClassName}
                />
            </div>
            {!hideTags && memoizedTags}
        </div>
    )
}

/**
 * formatFilters
 * This functions helps us to format filters better.
 * Anything we want to append/remove from the filters object
 * before we send it to the API can abe done here
 *
 * @param {object} filters - filter object
 * @return {object}
 */
export const formatFilters = (filters: FilterValues): FilterValues => {
    return filters
    const res: FilterValues = {}
    for (const [key, value] of Object.entries(filters)) {
        if ((key == 'start_date' || key == 'end_date') && value.hasOwnProperty('last')) {
            res[key] = {
                // @ts-ignore ???
                last: value['last'],
            }
        } else {
            res[key] = value
        }
    }

    return res
}
